import React, { useMemo, useState } from "react";
import { FaSearch } from "react-icons/fa";

import {
  filterSettingsTabs,
  groupTabsByCategory,
} from "../utils/settingsDashboardUtils";

export default function SettingsTabsShell({ tabs, activeTab, setActiveTab }) {
  const [tabSearch, setTabSearch] = useState("");

  const filteredTabs = useMemo(
    () => filterSettingsTabs(tabs, tabSearch),
    [tabs, tabSearch]
  );

  const groupedTabs = useMemo(
    () => groupTabsByCategory(filteredTabs),
    [filteredTabs]
  );

  const activeItem = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const ActiveComponent = activeItem?.Component;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="grid min-h-[620px] lg:grid-cols-[320px,1fr]">
        <aside className="border-b border-slate-200 bg-slate-50/80 p-4 dark:border-slate-800 dark:bg-slate-950/40 lg:border-b-0 lg:border-r">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Sections
            </p>

            <h2 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
              Paramètres
            </h2>

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Sélectionnez une rubrique à configurer. Sur mobile, utilisez la
              liste déroulante.
            </p>
          </div>

          <div className="mt-4 lg:hidden">
            <label
              htmlFor="settings-tab-mobile"
              className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400"
            >
              Rubrique
            </label>

            <select
              id="settings-tab-mobile"
              value={activeTab}
              onChange={(event) => setActiveTab(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              {tabs.map((tab) => (
                <option key={tab.key} value={tab.key}>
                  {tab.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 hidden lg:block">
            <div className="relative">
              <input
                type="text"
                value={tabSearch}
                onChange={(event) => setTabSearch(event.target.value)}
                placeholder="Rechercher un onglet..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />

              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            <div className="mt-4 max-h-[720px] space-y-5 overflow-y-auto pr-1">
              {Object.entries(groupedTabs).map(([group, items]) => (
                <div key={group}>
                  <p className="mb-2 px-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                    {group}
                  </p>

                  <div className="space-y-1.5">
                    {items.map(({ key, label, description, Icon }) => {
                      const active = activeTab === key;

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setActiveTab(key)}
                          className={`group flex w-full items-start gap-3 rounded-2xl px-3 py-3 text-left transition ${
                            active
                              ? "bg-[#013E8E] text-white shadow-sm dark:bg-indigo-500"
                              : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-800 dark:hover:bg-slate-800"
                          }`}
                        >
                          <span
                            className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                              active
                                ? "bg-white/15 text-white"
                                : "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                            }`}
                          >
                            <Icon />
                          </span>

                          <span className="min-w-0">
                            <span className="block text-sm font-bold">
                              {label}
                            </span>

                            {description ? (
                              <span
                                className={`mt-1 line-clamp-2 block text-xs leading-5 ${
                                  active
                                    ? "text-white/80"
                                    : "text-slate-500 dark:text-slate-400"
                                }`}
                              >
                                {description}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {!filteredTabs.length ? (
                <p className="rounded-2xl bg-white p-4 text-sm text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-800">
                  Aucun onglet trouvé.
                </p>
              ) : null}
            </div>
          </div>
        </aside>

        <main className="min-w-0 p-4 sm:p-5 lg:p-6">
          <div className="mb-5 rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Onglet actif
                </p>

                <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
                  {activeItem?.Icon ? <activeItem.Icon /> : null}
                  {activeItem?.label}
                </h3>

                {activeItem?.description ? (
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                    {activeItem.description}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div id={`panel-${activeTab}`} role="tabpanel">
            {ActiveComponent ? <ActiveComponent /> : null}
          </div>
        </main>
      </div>
    </section>
  );
}